import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BookOpen, FileText, Lock, ArrowRight, CalendarClock, GraduationCap } from "lucide-react";
import MarketingShell from "@/components/marketing/MarketingShell";
import { InterestForm } from "@/components/marketing/InterestForm";
import { useAuth } from "@/context/AuthContext";
import { http, trackEvent } from "@/lib/api";
import { useSeo, StructuredData } from "@/lib/seo";
import { courseItem } from "@/lib/checkout";
import { FacultyOption } from "@/components/marketing/FacultyOption";

const TIER_LABEL = { clarity: "Enrolled students", blueprint: "Blueprint lesson", guided: "Guided lesson", inhome_overnight: "In-Home lesson" };

const COURSE_SEO = {
  "is-101": ["Infant Sleep Basics Course (IS-101) | Online Infant Sleep Course",
    "Learn how infant sleep works: night wakings, short naps, bedtime patterns and a safer sleep space. Self-paced online course. Start right after enrollment.",
    "how infant sleep develops, what night wakings and short naps mean, bedtime and settling patterns, and setting up a safer sleep environment"],
  "ns-101": ["Baby Nap Problems Course (NS-101) | Naps & Wake Windows",
    "Short naps, nap refusal, wake windows and nap transitions explained. Self-paced online course on naps and daily rhythm. Coursework opens right after enrollment.",
    "short or unpredictable naps, wake windows and sleep pressure, nap transitions, and building a flexible daily rhythm"],
  "is-201": ["Baby Night Wakings Course (IS-201) | Independent Settling",
    "Understand frequent night wakings: sleep associations, responsive settling and night feeding, within safe-sleep guidance. Self-paced; start after enrollment.",
    "frequent night wakings, sleep-onset associations, responsive settling, night feeding and comfort decisions, and reviewing patterns across several nights"],
  "is-301": ["Baby Sleep Regressions Course (IS-301) | Developmental Changes",
    "What sleep regressions really are: sleep maturation, new skills, separation, illness and teething. Self-paced online course that opens right after enrollment.",
    "sudden sleep changes often called regressions, sleep maturation, new motor and cognitive skills, separation responses, illness and teething, and nap transitions"],
  "is-401": ["Bedtime & Early Waking Course (IS-401) | Crib Transition",
    "Bedtime resistance, false starts, early waking and moving to a crib or toddler bed. Self-paced online course by Andrea Hurley. Start right after enrollment.",
    "bedtime resistance and false starts, bedtime routines, early-morning waking, and safe transitions from bassinet to crib and crib to toddler bed"],
};

export default function CoursePage() {
  const { slug } = useParams();
  const [c, setC] = useState(undefined);
  useEffect(() => { http.get(`/courses/${slug}`).then((r) => setC(r.data)).catch(() => setC(null)); }, [slug]);
  useEffect(() => { if (c) trackEvent("view_item", { currency: "USD", value: courseItem(`${c.slug.replace("-", "")}_tuition`).value, course_code: c.code, item_list_name: "Course pages", items: [courseItem(`${c.slug.replace("-", "")}_tuition`).items[0], courseItem(`${c.slug.replace("-", "")}_faculty`).items[0]] }, { once: true, key: `view-${c.slug}` }); }, [c]);
  useSeo({
    title: COURSE_SEO[slug]?.[0] || (c ? `${c.code} ${c.title} | Infant Sleep Consultant Near Me` : "Course | Infant Sleep Consultant Near Me"),
    description: COURSE_SEO[slug]?.[1] || (c ? c.description : ""),
    canonical: `/courses/${slug}`,
  });

  if (c === undefined) return <MarketingShell><div className="min-h-[60vh]" /></MarketingShell>;
  if (c === null) return (
    <MarketingShell><div className="max-w-3xl mx-auto px-5 py-32 text-center" data-testid="course-not-found">
      <h1 className="font-academic text-4xl text-[#F5F2E9]">Course not found</h1>
      <Link to="/courses" className="btn-clay inline-block mt-8 px-6 py-3">View the Course Catalog</Link>
    </div></MarketingShell>
  );

  return (
    <MarketingShell>
      <StructuredData data={{
        "@context": "https://schema.org", "@type": "Course", name: `${c.code} ${c.title}`, description: c.description, courseCode: c.code,
        provider: { "@type": "Organization", name: "Infant Sleep Consultant Near Me Education Center", url: "https://infantsleepconsultantnearme.com/" },
      }} />
      <CourseHero c={c} />
      <section className="bg-[#F5F2E9]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-[1fr_340px] gap-12">
          <div>
            <Outcomes c={c} />
            <Details c={c} />
            <h2 className="font-academic text-3xl text-[#172A3A] font-semibold mt-14 mb-6">Syllabus</h2>
            <div className="space-y-5" data-testid="course-syllabus">{c.units.map((u) => <Unit key={u.number} u={u} />)}</div>
          </div>
          <EnrollCard c={c} />
        </div>
      </section>
    </MarketingShell>
  );
}

function CourseHero({ c }) {
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-14">
      <Link to="/courses" data-testid="course-back" className="text-[12px] uppercase tracking-[0.16em] text-[#B9C0C9] hover:text-white">← Course Catalog</Link>
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <span className="font-mono text-[13px] tracking-[0.2em] text-[#BDA677] border border-[#BDA677]/50 px-3 py-1" data-testid="course-code">{c.code}</span>
        <span className="text-[11px] uppercase tracking-[0.16em] text-[#B9C0C9]">{c.level}</span>
      </div>
      <h1 className="font-academic text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#F5F2E9] tracking-tight mt-5 leading-[1.02]" data-testid="course-title">{c.title}</h1>
      <p className="text-[#B9C0C9] mt-6 max-w-3xl text-base sm:text-[17px] leading-relaxed">{c.description}</p>
      {COURSE_SEO[c.slug] && <p className="text-[#C9CFD6] mt-4 max-w-3xl text-sm sm:text-base leading-relaxed" data-testid="course-challenges"><span className="text-[#F5F2E9] font-medium">Sleep challenges covered:</span> {COURSE_SEO[c.slug][2]}. Self-paced coursework opens immediately after successful enrollment.</p>}
      <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 text-sm text-[#C9CFD6]">
        <span className="flex items-center gap-2"><CalendarClock size={15} className="text-[#BDA677]" /> {c.format}</span>
        <span className="flex items-center gap-2"><BookOpen size={15} className="text-[#BDA677]" /> {c.unit_count} units · {c.reading_count} required reading{c.reading_count === 1 ? "" : "s"}{c.lesson_count ? ` · ${c.lesson_count} lessons` : ""}</span>
        <span className="flex items-center gap-2" data-testid="course-status"><GraduationCap size={15} className="text-[#BDA677]" /> {c.status}{c.faculty ? ` · Faculty-led ${c.faculty.tuition}` : ""}</span>
      </div>
    </section>
  );
}

function Outcomes({ c }) {
  return (
    <div>
      <div className="eyebrow text-[#9C8556] mb-4">Learning outcomes</div>
      <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="course-outcomes">
        {c.outcomes.map((o) => (
          <li key={o} className="flex gap-3 text-[#172A3A] text-[15px] leading-relaxed"><span className="text-[#BDA677] font-academic text-lg leading-none mt-0.5">§</span>{o}</li>
        ))}
      </ul>
    </div>
  );
}

function Details({ c }) {
  const rows = [["Instructor", `${c.instructor.name}, ${c.instructor.title}`], ["Format", c.format], ["Enrollment status", c.status]];
  return (
    <div className="mt-12 grid md:grid-cols-2 gap-8" data-testid="course-details">
      <dl className="space-y-4">
        {rows.map(([k, v]) => (
          <div key={k}><dt className="text-[11px] uppercase tracking-[0.16em] text-[#647080]">{k}</dt><dd className="text-[#172A3A] text-[15px] mt-1">{v}</dd></div>
        ))}
      </dl>
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#647080]">Coursework</div>
        <ul className="mt-2 space-y-1.5" data-testid="course-coursework">
          {c.coursework.map((w) => <li key={w} className="flex gap-2 text-[#172A3A] text-[15px]"><span className="text-[#BDA677]">—</span>{w}</li>)}
        </ul>
      </div>
    </div>
  );
}

function Unit({ u }) {
  return (
    <div className="bg-white border border-[#172A3A]/12 p-6 sm:p-7" data-testid={`course-unit-${u.number}`}>
      <div className="flex items-baseline gap-4">
        <span className="font-academic text-[#BDA677] text-2xl font-semibold">{String(u.number).padStart(2, "0")}</span>
        <h3 className="font-academic text-2xl text-[#172A3A] font-semibold">{u.title}</h3>
      </div>
      {u.lessons.length > 0 && (
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#647080] mb-2">Lessons</div>
          {u.lessons.map((l) => (
            <div key={l.key} className="flex items-start gap-3 py-2.5 border-t border-[#172A3A]/8" data-testid={`lesson-${l.key}`}>
              <Lock size={15} className="text-[#647080] mt-1 shrink-0" />
              <div className="flex-1"><div className="text-[#172A3A] text-[15px] font-medium">{l.title}</div><div className="text-[#647080] text-sm">{l.summary}</div></div>
              <span className="text-[10.5px] uppercase tracking-[0.12em] text-[#647080] whitespace-nowrap hidden sm:block">{TIER_LABEL[l.tier]}</span>
            </div>
          ))}
        </div>
      )}
      {u.readings.length > 0 && (
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#647080] mb-2">Required readings</div>
          <ul className="grid sm:grid-cols-2 gap-x-6">
            {u.readings.map((r) => (
              <li key={r.slug}><Link to={`/${r.slug}`} data-testid={`reading-${r.slug}`} className="flex items-start gap-2 py-1.5 text-[14px] text-[#172A3A] font-medium hover:text-[#9C8556] transition-colors"><FileText size={14} className="text-[#BDA677] mt-1 shrink-0" />{r.title}</Link></li>
            ))}
          </ul>
        </div>
      )}
      {u.optional.length > 0 && (
        <div className="mt-4 pt-4 border-t border-dashed border-[#172A3A]/12">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#8F9AA8] mb-1.5">Optional further reading</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {u.optional.map((r) => (
              <Link key={r.slug} to={`/${r.slug}`} data-testid={`optional-${r.slug}`} className="text-[13px] text-[#647080] hover:text-[#9C8556] underline decoration-[#172A3A]/15 underline-offset-4">{r.title}</Link>
            ))}
          </div>
        </div>
      )}
      {u.supplemental?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-dashed border-[#172A3A]/12" data-testid={`course-unit-${u.number}-supplemental`}>
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#9C8556] mb-1.5">Additional Reading From Andrea · Supplemental</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {u.supplemental.map((r) => (
              <Link key={r.slug} to={`/${r.slug}`} data-testid={`supplemental-${r.slug}`} className="text-[13px] text-[#647080] hover:text-[#9C8556] underline decoration-[#172A3A]/15 underline-offset-4">{r.title}</Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SelfPacedEnroll({ c, access }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const canceled = new URLSearchParams(window.location.search).get("checkout") === "canceled";
  const product = `${c.slug.replace("-", "")}_tuition`;
  const enroll = () => { trackEvent("select_item", { item_list_name: `${c.code} learning formats`, ...courseItem(product) }); nav(`/enroll/${product}`); };
  useEffect(() => { if (canceled && user?.role === "customer") http.post("/payments/release-hold").catch(() => {}); }, [canceled, user]);
  if (access) return (
    <>
      <p className="text-[#B9C0C9] text-sm leading-relaxed mt-3" data-testid="course-enrolled-note">You are enrolled in this course. Your lessons, assessments and saved progress are in your Learning Center.</p>
      <button onClick={() => nav(`/portal/resources?course=${c.slug}`)} data-testid="course-enroll-btn" className="btn-clay w-full py-3.5 mt-6 flex items-center justify-center gap-2">Go to My Lessons <ArrowRight size={16} /></button>
    </>
  );
  if (c.checkout_open) return (
    <>
      {canceled && <div className="mt-3 text-sm text-[#F5F2E9] border border-[#BDA677]/50 px-3 py-2" data-testid="checkout-canceled">Checkout canceled — no charge was made. You can enroll whenever you're ready.</div>}
      <button onClick={enroll} data-testid="course-enroll-btn" className="btn-clay w-full py-3.5 mt-5 flex items-center justify-center gap-2">{`Enroll in Independent Study — ${c.tuition}`} <ArrowRight size={16} /></button>
      {!user && <p className="text-[#8F9AA8] text-xs mt-3">You'll create a free student account first, then continue to checkout.</p>}
    </>
  );
  return (
    <>
      <div className="mt-2 text-[11px] uppercase tracking-[0.14em] text-[#BDA677]" data-testid="course-enrollment-soon">Tuition Enrollment Opening Soon</div>
      <div className="mt-4"><InterestForm defaultCourse={c.slug} selfPaced /></div>
    </>
  );
}

const IS_INCLUDES = ["Complete written course curriculum", "Immediate access after verified payment", "Self-guided lessons, readings and coursework",
  "Automatically scored assessments", "Saved academic progress", "Sleep Observation Tracker", "No scheduled class attendance"];

function EnrollCard({ c }) {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  useEffect(() => { if (user?.role === "customer") http.get("/portal/courses/access").then((r) => setAccess(r.data.courses?.[c.slug]?.access)).catch(() => {}); }, [user, c.slug]);
  return (
    <aside className="lg:sticky lg:top-28 self-start bg-[#172A3A] text-[#F5F2E9] p-7 border-t-4 border-[#BDA677]" data-testid="course-enroll-card">
      <GraduationCap size={26} className="text-[#BDA677]" />
      <div className="eyebrow text-[#BDA677] mt-4">Choose your learning format</div>
      <div className="mt-4" data-testid="option-independent">
        <div className="eyebrow text-[#BDA677]">Option A · Independent Study</div>
        <div className="font-academic text-3xl font-semibold mt-1" data-testid="course-tuition">{c.tuition}</div>
        <div className="mt-1 text-[12px] text-[#B9C0C9]" data-testid="course-tuition-note">per family · up to two caregivers · enroll anytime</div>
        <ul className="mt-3 space-y-1.5 text-sm text-[#C9CFD6]">{IS_INCLUDES.map((t) => <li key={t} className="flex gap-2"><span className="text-[#BDA677]">—</span>{t}</li>)}</ul>
        <SelfPacedEnroll c={c} access={access} />
      </div>
      <FacultyOption c={c} hasAccess={!!access} />
      <div className="mt-7 pt-5 border-t border-[#2B4053] flex items-center gap-3" data-testid="course-instructor">
        <img src="/images/andrea-hurley.jpg" alt="Andrea Hurley" className="w-12 h-12 rounded-full object-cover object-top border border-[#BDA677]/60" />
        <div><div className="text-[#F5F2E9] text-sm font-medium">Andrea Hurley</div><div className="text-[#8F9AA8] text-[11px] uppercase tracking-[0.12em]">Founding Instructor · Certified Pediatric Sleep Consultant</div></div>
      </div>
    </aside>
  );
}
